/**
 * ImageUploader Component
 *
 * Allows users to add images via URL or file upload
 * Supports JPG, PNG, WebP up to 2MB
 */

import { Stack, TextInput, Button, Group, Text, Paper, SegmentedControl, FileInput, Alert } from "@mantine/core";
import { IconLink, IconUpload, IconAlertCircle, IconPhoto } from "@tabler/icons-react";
import { useState, useCallback } from "react";
import { addImageUrlSchema } from "../../forms/template-schema";
import { validateImageFile } from "../../utils/storage";

type UploadMode = "url" | "upload";

export interface ImageUploaderProperties {
  /** Callback when image is added via URL */
  onAddUrl: (data: { nome: string; url: string }) => Promise<unknown>;
  /** Callback when image is uploaded */
  onUpload: (data: { nome: string; file: File }) => Promise<unknown>;
  /** Whether URL add is in progress */
  isAddingUrl?: boolean;
  /** Whether upload is in progress */
  isUploading?: boolean;
  /** Existing image names (for duplicate check) */
  existingNames?: string[];
}

/**
 * ImageUploader - Add images via URL or file upload
 * @param root0
 * @param root0.onAddUrl
 * @param root0.onUpload
 * @param root0.isAddingUrl
 * @param root0.isUploading
 * @param root0.existingNames
 */
export function ImageUploader({
  onAddUrl,
  onUpload,
  isAddingUrl = false,
  isUploading = false,
  existingNames = []
}: ImageUploaderProperties) {
  const [mode, setMode] = useState<UploadMode>("url");
  const [nome, setNome] = useState("");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isLoading = isAddingUrl || isUploading;

  const resetForm = useCallback(() => {
    setNome("");
    setUrl("");
    setFile(null);
    setError(null);
  }, []);

  const handleAddUrl = useCallback(async () => {
    setError(null);

    // Validate with Zod
    const validation = addImageUrlSchema.safeParse({ nome, url });
    if (!validation.success) {
      const firstError = validation.error.errors[0];
      setError(firstError?.message ?? "Dados inválidos");
      return;
    }

    // Check for duplicate name
    const normalizedName = nome.toLowerCase().replaceAll(/[^\w-]/g, "_");
    if (existingNames.includes(normalizedName)) {
      setError(`Já existe uma imagem com o nome "${normalizedName}"`);
      return;
    }

    try {
      await onAddUrl({ nome, url });
      resetForm();
    } catch (error_) {
      setError(error_ instanceof Error ? error_.message : "Erro ao adicionar imagem");
    }
  }, [nome, url, existingNames, onAddUrl, resetForm]);

  const handleUpload = useCallback(async () => {
    setError(null);

    if (!nome.trim()) {
      setError("Nome é obrigatório");
      return;
    }

    if (!file) {
      setError("Selecione um arquivo");
      return;
    }

    // Validate file
    const validation = validateImageFile(file);
    if (!validation.valid) {
      setError(validation.error ?? "Arquivo inválido");
      return;
    }

    // Check for duplicate name
    const normalizedName = nome.toLowerCase().replaceAll(/[^\w-]/g, "_");
    if (existingNames.includes(normalizedName)) {
      setError(`Já existe uma imagem com o nome "${normalizedName}"`);
      return;
    }

    try {
      await onUpload({ nome, file });
      resetForm();
    } catch (error_) {
      setError(error_ instanceof Error ? error_.message : "Erro no upload");
    }
  }, [nome, file, existingNames, onUpload, resetForm]);

  const handleFileChange = useCallback(
    (selectedFile: File | null) => {
      setFile(selectedFile);
      setError(null);

      // Auto-fill name from filename if empty
      if (selectedFile && !nome) {
        const nameWithoutExtension = selectedFile.name.replace(/\.[^./]+$/, "");
        setNome(nameWithoutExtension);
      }
    },
    [nome]
  );

  const handleModeChange = useCallback((value: string) => {
    setMode(value as UploadMode);
    setError(null);
  }, []);

  const handleNomeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setNome(e.currentTarget.value);
  }, []);

  const handleUrlChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(e.currentTarget.value);
  }, []);

  return (
    <Paper p="md" withBorder>
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Text fw={500} size="sm">
            Adicionar Imagem
          </Text>
          <SegmentedControl
            size="xs"
            value={mode}
            onChange={handleModeChange}
            data={[
              { label: "URL", value: "url" },
              { label: "Upload", value: "upload" }
            ]}
          />
        </Group>

        <TextInput
          label="Nome da imagem"
          description="Use apenas letras, números, underscores e hífens"
          placeholder="capa_relatorio"
          value={nome}
          onChange={handleNomeChange}
          disabled={isLoading}
          leftSection={<IconPhoto size={16} />}
        />

        {mode === "url" ? (
          <TextInput
            label="URL da imagem"
            description="URL completa da imagem (https://...)"
            placeholder="https://exemplo.com/imagem.jpg"
            value={url}
            onChange={handleUrlChange}
            disabled={isLoading}
            leftSection={<IconLink size={16} />}
          />
        ) : (
          <FileInput
            label="Arquivo"
            description="JPG, PNG ou WebP (máx. 2MB)"
            placeholder="Selecione uma imagem"
            value={file}
            onChange={handleFileChange}
            disabled={isLoading}
            accept="image/jpeg,image/png,image/webp"
            leftSection={<IconUpload size={16} />}
          />
        )}

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            {error}
          </Alert>
        )}

        <Button
          onClick={mode === "url" ? handleAddUrl : handleUpload}
          loading={isLoading}
          leftSection={mode === "url" ? <IconLink size={16} /> : <IconUpload size={16} />}
          disabled={!nome.trim() || (mode === "url" ? !url.trim() : !file)}
        >
          {mode === "url" ? "Adicionar URL" : "Fazer Upload"}
        </Button>
      </Stack>
    </Paper>
  );
}
